import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UserSubscriptionDTO, VerifyOTPDTO, VerifyRecaptchaDTO } from "../dto/auth";
import { AuthApi } from "../api/auth";
import { useFetcher } from "@module/_core/infras/hook/useFetcher";
import { useEffect } from "react";

export class AuthService {
    static USER_KEY = "USER";
    static ACCESS_TOKEN = "ACCESS_TOKEN";
    static REFRESH_TOKEN = "REFRESH_TOKEN";

    // Local Storage
    static storage = {
        getUser(): UserSubscriptionDTO | undefined {
            if (typeof window === "undefined") return;
            const raw = localStorage.getItem(AuthService.USER_KEY);
            if (!raw) return;

            try {
                return JSON.parse(raw) as UserSubscriptionDTO;
            } catch (e) {
                console.warn("Invalid USER JSON in localStorage, clearing...");
                localStorage.removeItem(AuthService.USER_KEY);
                return;
            }
        },

        getAccessToken() {
            if (typeof window === "undefined") return;
            const raw = localStorage.getItem(AuthService.ACCESS_TOKEN);
            return raw;
        },

        getRefreshToken() {
            if (typeof window === "undefined") return;
            const raw = localStorage.getItem(AuthService.REFRESH_TOKEN);
            return raw;
        },

        setUser(userSubscription: UserSubscriptionDTO) {
            localStorage.setItem(AuthService.USER_KEY, JSON.stringify(userSubscription));
        },

        setAccessToken(token: string) {
            localStorage.setItem(AuthService.ACCESS_TOKEN, token);
        },

        setRefreshToken(refreshToken: string) {
            localStorage.setItem(AuthService.REFRESH_TOKEN, refreshToken);
        },

        clear() {
            localStorage.removeItem(AuthService.USER_KEY);
            localStorage.removeItem(AuthService.ACCESS_TOKEN);
            localStorage.removeItem(AuthService.REFRESH_TOKEN);
        }
    };

    static isRefreshing = false;

    static useMe = () => {
        const { refreshTokenMutation } = AuthService.useAuthAction();
        const initUserData = AuthService.storage.getUser();

        const { data, isError, error, isSuccess, isFetching } = useFetcher(
            [AuthService.USER_KEY],
            AuthApi.getUserInfo,
            {
                enabled: !!initUserData,
                staleTime: 120000,
                gcTime: Infinity
            }
        );

        useEffect(() => {
            if (!!data && isSuccess) AuthService.storage.setUser(data);
        }, [data, isSuccess]);

        useEffect(() => {
            if ((error as any)?.code !== 401008) return;
            if (AuthService.isRefreshing) return;

            AuthService.isRefreshing = true;

            const refreshToken = AuthService.storage.getRefreshToken();
            if (!refreshToken) {
                AuthService.storage.clear();
                AuthService.isRefreshing = false;
                return;
            }

            refreshTokenMutation.mutate(refreshToken, {
                onSuccess: () => {
                    AuthService.isRefreshing = false;
                },
                onError: () => {
                    AuthService.isRefreshing = false;
                    AuthService.storage.clear();
                }
            });
        }, [error]);

        return {
            isLogin: !!initUserData,
            isFetching,
            data,
            isError,
            error
        };
    };

    // Auth Actions
    static useAuthAction = () => {
        const queryClient = useQueryClient();

        const verifyRecaptchaMutation = useMutation({
            mutationFn: (payload: VerifyRecaptchaDTO) => AuthApi.verifyRecaptcha(payload)
        });

        const verifyOTPMutation = useMutation({
            mutationFn: (payload: VerifyOTPDTO) => AuthApi.verifyOTP(payload),

            onSuccess: (data) => {
                AuthService.storage.setUser(data);
                AuthService.storage.setAccessToken(data.accessToken);
                AuthService.storage.setRefreshToken(data.refreshToken);

                queryClient.setQueryData([AuthService.USER_KEY], data);
            }
        });

        const refreshTokenMutation = useMutation({
            mutationFn: (refreshToken: string) => AuthApi.refreshToken(refreshToken),

            onSuccess: (data) => {
                if (!data) return;
                AuthService.storage.setUser(data);
                AuthService.storage.setAccessToken(data.accessToken);
                AuthService.storage.setRefreshToken(data.refreshToken);

                queryClient.setQueryData([AuthService.USER_KEY], data);
            }
        });

        return {
            refreshTokenMutation,
            verifyRecaptchaMutation,
            verifyOTPMutation
        };
    };

    // Logout
    static logout = () => {
        location.replace("/");
        AuthService.storage.clear();
    };
}
